import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import NotSupportedFormatMask from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader/NotSupportedFormatMask'
import UploadCvIcon from '@core/components/ApplyDialogV2/assets/UploadCvIcon'
import DefaultMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/DefaultMaskIcon'
import UnsupportedFileMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/UnsupportedFileMaskIcon'
import FileUploaderV2ListItem from '@core/components/ApplyDialogV2/FileUploaderV2ListItem'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './ApplyV2AttachmentsUploader2.scss'
import { useTranslation } from 'react-i18next'

interface ErrorObject {
    size?: string
    name?: string
    type?: string
    base64?: string
    ext?: string
    [key: string]: any
}

interface Props {
    id?: string | number
    label?: React.ReactNode
    rightLabel?: React.ReactNode
    name?: string
    files?: (V2CourseFile | ApplyV2TempBase64File)[] | null
    className?: string
    onDelete?: (file: V2CourseFile | File | ApplyV2TempBase64File) => void | Promise<void>
    isShowDelete?: boolean
    onChange?: (files: ApplyV2TempBase64File[]) => void
    // onChange?: React.ChangeEventHandler<HTMLInputElement>
    // onChangeFiles?: (files: File[]) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    errorMessage?: string | string[] | Array<ErrorObject | null> | null | boolean | undefined
    isLoading?: boolean
    isDisabled?: boolean
    children?: React.ReactNode
    isShowErrorMessage?: boolean
    description?: React.ReactNode
    isRequired?: boolean
    isShowInput?: boolean
    isShowList?: boolean
    accept?: string[]
    maxFileSize?: number
    multipleCount?: number
    // isBase64: boolean
    loadingIds?: (string | number)[]
    uploadingCount?: number
    filesErrors?: ({ error: string } & (V2CourseFile | ApplyV2TempBase64File))[]
    progressItems?: { index: number; progress: number }[]
}

const baseClass = 'ApplyV2AttachmentsUploader2'

function ApplyV2AttachmentsUploader2({
    files = [],
    className,
    id,
    label,
    rightLabel,
    name,
    onDelete,
    onChange,
    onFocus,
    onBlur,
    isShowDelete = false,
    errorMessage,
    isLoading = false,
    isDisabled = false,
    children,
    isShowErrorMessage = true,
    description,
    isRequired = false,
    isShowInput = true,
    isShowList = true,
    accept = [],
    maxFileSize = 20 * 1024 * 1024,
    multipleCount = 1,
    // isBase64 = true,
    loadingIds = [],
    uploadingCount = 0,
    filesErrors = [],
    progressItems = [],
}: Props) {
    const usedId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const mockInputRef = useRef<HTMLDivElement>(null)
    const [innerError, setInnerError] = useState('')
    const [isUnsupportedFormat, setIsUnsupportedFormat] = useState(false)
    const [isOnDrag, setIsOnDrag] = useState(false)
    const [isNarrow, setIsNarrow] = useState(false)

    const errorMessageRes = useMemo(() => {
        if (!errorMessage) return ''

        if (typeof errorMessage === 'string') {
            return errorMessage
        }

        if (Array.isArray(errorMessage)) {
            // console.log('IS ARRAY', errorMessage)
            const messages = errorMessage
                .map((item) => {
                    if (typeof item === 'object' && item !== null) {
                        return Object.values(item).find((val) => typeof val === 'string') || ''
                    }
                    return typeof item === 'string' ? item : ''
                })
                .filter(Boolean)

            return messages.join(', ')
        }

        return typeof errorMessage === 'boolean' && errorMessage ? 'An error occurred' : ''
    }, [errorMessage, getTitle])

    const defaultId = id ? id.toString() : usedId
    useLayoutEffect(() => {
        const checkWidth = () => {
            if (containerRef.current) {
                setIsNarrow(containerRef.current.offsetWidth < 500)
            }
        }

        checkWidth()
        window.addEventListener('resize', checkWidth)

        return () => {
            window.removeEventListener('resize', checkWidth)
        }
    }, [])

    const isFileAccepted = (file: File): boolean => {
        if (!accept.length) return true
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
        return accept.some(
            (ext) =>
                ext.toLowerCase() === fileExtension ||
                ext.toLowerCase() === `.${fileExtension}` ||
                ext.toLowerCase() === file.type
        )
    }

    const validateFiles = (fils: File[]): string | null => {
        const errors = []
        if ((files ?? []).length + fils.length > multipleCount) {
            errors.push(`${multipleCount} files allowed`)
        }

        if (accept.length > 0) {
            const invalidFiles = fils.filter((file) => !isFileAccepted(file))
            if (invalidFiles.length > 0) {
                errors.push(getTitle('6200', 'File format is not supported'))
            }
        }

        const oversizedFiles = fils.filter((file) => file.size > maxFileSize * 1024 * 1024)
        if (oversizedFiles.length > 0) {
            console.log('oversized files', oversizedFiles)
            // errors.push(`Some files exceed maximum size, - ${maxFileSize} MB`)
            errors.push(
                t('10533', 'Some files exceed maximum size, - {{maxFileSize}} MB', { maxFileSize: maxFileSize })
            )
        }

        return errors.join(', ')
    }

    const onDragEnter = (e: React.DragEvent<HTMLInputElement | HTMLDivElement>) => {
        console.log('onDragEnter')
        setIsOnDrag(true)
        const items = e.dataTransfer.items
        const invalidFiles: string[] = []

        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.kind === 'file') {
                const mimeType = item.type
                if (mimeType) {
                    const mainType = mimeType.split('/')[0]
                    if (!accept.some((ext) => mimeType.includes(ext.replace('.', '')))) {
                        invalidFiles.push(`INVALID FORMAT`)
                    }
                }
            }
        }

        console.log('onDragEnter', invalidFiles)

        if (invalidFiles.length > 0) {
            setInnerError(getTitle('6200', 'File format is not supported'))
        } else {
            setInnerError('')
        }
    }
    const onDragLeave = (e: React.DragEvent<HTMLInputElement>) => {
        console.log('onDragLeave')
        setIsOnDrag(false)
        setInnerError('')
    }
    const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
        console.log('onDrop')
        setIsOnDrag(false)
        setInnerError('')

        if (inputRef?.current) {
            inputRef.current.value = ''
        }
    }

    const onFocusMiddleware = (e: React.FocusEvent<HTMLInputElement>) => {
        console.log('onFocusMiddleware')
        // setInnerError('')
        onFocus && onFocus(e)
        setInnerError('')
    }

    const onBlurMiddleware = (e: React.FocusEvent<HTMLInputElement>) => {
        console.log('onBlurMiddleware')
        setInnerError('')
        onBlur && onBlur(e)
    }

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                const result = reader.result as string
                const base64 = result.split('base64,')[1]
                resolve(base64)
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    const convertToBase64Promise = async (
        files: File[]
    ): Promise<{ name: string; ext: string; base64: string; lastModified: number; size: number }[]> => {
        return Promise.all(
            files.map(async (file) => {
                const base64 = await fileToBase64(file)
                const ext = file.name.split('.').pop() || file.type.split('/').pop() || ''
                return {
                    name: file.name,
                    ext,
                    base64,
                    lastModified: file.lastModified,
                    size: file.size,
                }
            })
        )
    }

    const onDeleteMiddleware = (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => {
        // console.log('onDeleteMiddleware')
        // console.log(file)

        if ('uid' in file && 'ext' in file && onDelete) {
            onDelete(file)
        } else if (file instanceof File) {
            console.log('Deleting File object:', file.name)
            // onDelete && onDelete(file as File)
            onDelete && onDelete(file)
        } else {
            console.log('Deleting other file type')
            onDelete && onDelete(file)
        }
        console.log(innerError, 'delete')
        setInnerError('')
    }

    const onChangeMiddleware = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('onChangeMiddleware')
        setInnerError('')
        setIsOnDrag(false)
        try {
            const filesArray = Array.from(e.target.files ?? [])
            const validationError = validateFiles(filesArray)
            console.log('validationError', validationError)
            if (validationError && filesArray.length === 1) {
                setInnerError(validationError)
                return
            } else {
                setInnerError('')
            }

            convertToBase64Promise(filesArray).then((res) => {
                onChange && onChange(res)
            })
        } catch (error) {
            console.error('Error handling file upload:', error)
            setInnerError('Failed to process files')
        } finally {
            e.target.value = ''
        }
    }

    return (
        <div
            ref={containerRef}
            className={clsx(baseClass, className, {
                // [`${baseClass}--error`]: !!errorMessageRes || !!innerError,
                [`${baseClass}--error`]: !!errorMessageRes,
                // [`${className}--error`]: (!!errorMessageRes || !!innerError) && !!className,
                [`${className}--error`]: !!errorMessageRes && !!className,
                [`${baseClass}--disabled`]: isDisabled,
                [`${className}--disabled`]: !!className && isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${className}--loading`]: !!className && isLoading,
                [`${baseClass}--inputHidden`]: !isShowInput,
                [`${baseClass}--inputHidden`]: !isShowInput && !!className,
            })}
        >
            {(label || rightLabel) && (
                <div
                    className={clsx(`${baseClass}__label`, {
                        [`${className}__label`]: !!className,
                    })}
                >
                    <label
                        className={clsx(`${baseClass}__label--text`, {
                            [`${className}__label--text`]: !!className,
                        })}
                        htmlFor={id ? id.toString() : defaultId}
                    >
                        {label}
                        {isRequired && '*'}
                    </label>
                    {rightLabel && <span>{rightLabel}</span>}
                    {/*<span*/}
                    {/*    className={clsx(`${baseClass}__label--text`, {*/}
                    {/*        [`${className}__label--text`]: !!className,*/}
                    {/*    })}*/}
                    {/*>*/}
                    {/*    {label}*/}
                    {/*    {isRequired && '*'}*/}
                    {/*</span>*/}
                </div>
            )}
            {isShowInput && (
                <div
                    className={clsx(`${baseClass}__wrapper`, {
                        [`${className}__wrapper`]: !!className,
                        // [`${baseClass}__wrapper--error`]: !!errorMessageRes || !!innerError,
                        [`${baseClass}__wrapper--error`]: !!errorMessageRes,
                        [`${baseClass}__wrapper--innerError`]: !!innerError,
                        // [`${className}__wrapper--error`]: (!!errorMessageRes || !!innerError) && !!className,
                        [`${className}__wrapper--error`]: !!errorMessageRes && !!className,
                        [`${className}__wrapper--innerError`]: !!innerError && !!className,
                        [`${baseClass}__wrapper--disabled`]: isDisabled,
                        [`${className}__wrapper--disabled`]: !!className && isDisabled,
                        [`${baseClass}__wrapper--filesExist`]: (files ?? []).length > 0,
                    })}
                >
                    {isLoading && (
                        <div
                            className={clsx(`${baseClass}__wrapper__loader`, {
                                [`${className}__wrapper__loader`]: className && isLoading,
                            })}
                        ></div>
                    )}
                    <div
                        className={clsx(`${baseClass}__wrapper__content`, {
                            [`${className}__wrapper__content`]: !!className,
                            [`${baseClass}__wrapper__content--disabled`]: !isDisabled,
                            [`${className}__wrapper__content--disabled`]: !!isDisabled && !!className,
                            // [`${baseClass}__wrapper__content--error`]: !!innerError || !!errorMessageRes,
                            [`${baseClass}__wrapper__content--error`]: !!errorMessageRes,
                            [`${baseClass}__wrapper__content--innerError`]: !!innerError,
                            // [`${className}__wrapper__content--error`]:
                            //     (!!innerError || !!errorMessageRes) && !!className,
                            [`${className}__wrapper__content--error`]: !!errorMessageRes && !!className,
                            [`${className}__wrapper__content--innerError`]: !!innerError && !!className,
                        })}
                        // onClick={(e) => {
                        //     // handleClick()
                        // }}
                    >
                        <input
                            ref={inputRef}
                            id={defaultId}
                            type="file"
                            onError={(e) => {
                                console.log('Input FILE error')
                            }}
                            // onDragEnter={onDragEnter}
                            // onDragLeave={onDragLeave}
                            // onDragOver={(e) => {
                            //     console.log('onDragOver')
                            // }}
                            // onDrop={onDrop}
                            name={name}
                            multiple={multipleCount > 1}
                            accept={accept?.join(',')}
                            onChange={onChangeMiddleware}
                            onFocus={onFocusMiddleware}
                            onBlur={onBlurMiddleware}
                            disabled={isDisabled}
                            className={clsx(`${baseClass}__wrapper__input`, {
                                // [`${baseClass}__wrapper__input--error`]: !!errorMessageRes || !!innerError,
                                [`${baseClass}__wrapper__input--error`]: !!errorMessageRes,
                                [`${baseClass}__wrapper__input--innerError`]: !!innerError,
                                // [`${className}__wrapper__input--error`]:
                                //     className && (!!errorMessageRes || !!innerError),
                                [`${className}__wrapper__input--error`]: className && !!errorMessageRes,
                                [`${className}__wrapper__input--innerError`]: className && !!innerError,
                                [`${baseClass}__wrapper__input--loading`]: isLoading,
                                [`${className}__wrapper__input--loading`]: className && isLoading,
                                [`${baseClass}__wrapper__input--isOnDrag`]: isOnDrag,
                                [`${className}__wrapper__input--isOnDrag`]: className && isOnDrag,
                                [`${baseClass}__wrapper__input--inputHidden`]: !isShowInput,
                            })}
                        />
                        <div
                            className={clsx(`${baseClass}__wrapper__input--mock`, {
                                // [`${baseClass}__wrapper__input--error`]: !!errorMessageRes || !!innerError,
                                [`${baseClass}__wrapper__input--mock--error`]: !!errorMessageRes,
                                [`${baseClass}__wrapper__input--mock--innerError`]: !!innerError,
                                // [`${className}__wrapper__input--error`]:
                                //     className && (!!errorMessageRes || !!innerError),
                                [`${className}__wrapper__input--mock--error`]: className && !!errorMessageRes,
                                [`${className}__wrapper__input--mock--innerError`]: className && !!innerError,
                                [`${baseClass}__wrapper__input--mock--loading`]: isLoading,
                                [`${className}__wrapper__input--mock--loading`]: className && isLoading,
                                [`${baseClass}__wrapper__input--mock--isOnDrag`]: isOnDrag,
                                [`${className}__wrapper__input--mock--isOnDrag`]: className && isOnDrag,
                                [`${baseClass}__wrapper__input--mock--inputHidden`]: !isShowInput,
                            })}
                            onClick={() => {
                                inputRef.current?.click()
                            }}
                            onDragEnter={(e) => {
                                onDragEnter(e)
                            }}
                            onDragLeave={onDragLeave}
                            onDrop={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsOnDrag(false)
                                setInnerError('')

                                try {
                                    const filesArray = Array.from(e.dataTransfer.files ?? [])
                                    const validationError = validateFiles(filesArray)
                                    if (validationError && filesArray.length === 1) {
                                        setInnerError(validationError)
                                        return
                                    } else {
                                        setInnerError('')
                                    }

                                    convertToBase64Promise(filesArray).then((res) => {
                                        onChange && onChange(res)
                                    })
                                } catch (error) {
                                    console.error('Error handling file upload:', error)
                                    setInnerError('Failed to process files')
                                } finally {
                                    if (inputRef?.current) {
                                        inputRef.current.value = ''
                                    }
                                }
                            }}
                            onDragOver={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                            }}
                        />
                        {/*{innerError || errorMessageRes ? (*/}
                        {innerError ? (
                            <div className={`${baseClass}__inner`}>
                                <div className={`${baseClass}__inner__icon ${baseClass}__inner__icon-error`}>
                                    {/*<UnsupportedFileMaskIcon />*/}
                                    <NotSupportedFormatMask />
                                    {/*<WarningErrorIcon />*/}
                                    {/*<i className="fa-light fa-circle-exclamation" style={{ fontSize: '64px' }}></i>*/}
                                </div>
                                <p className={`${baseClass}__inner__text`}>
                                    {/*{innerError || errorMessageRes || getTitle('6200', 'File format is not supported')}*/}
                                    {innerError || getTitle('6200', 'File format is not supported')}
                                </p>
                            </div>
                        ) : (
                            <div className={`${baseClass}__inner`}>
                                {children || (
                                    <>
                                        <div className={`${baseClass}__inner__icon`}>
                                            <DefaultMaskIcon />
                                        </div>
                                        <p className={`${baseClass}__inner__text`}>
                                            {isOnDrag
                                                ? getTitle('10489', 'Release file to upload')
                                                : getTitle('10348', 'Drop files here or click to upload')}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}

            {files && files.length + uploadingCount > 0 && (
                <div
                    className={clsx(`${baseClass}__files`, {
                        [`${className}__files`]: !!className,
                    })}
                >
                    {files.map((file, index, array) => {
                        let error = (filesErrors ?? []).find((err) => {
                            if ('uid' in file && 'uid' in err) {
                                return err.uid === file.uid
                            }
                            return err.name === file.name
                        })?.error
                        // console.log('err', error)
                        const isLast = index === array.length - 1 + uploadingCount
                        const isOdd = (array.length + uploadingCount) % 2 === 1
                        const classNameItem = clsx(`${baseClass}__files__item`, {
                            [`${className}__files__item`]: className,
                            [`${baseClass}__files--narrow`]: isNarrow,
                            [`${className}__files--narrow`]: isNarrow && className,
                            [`${baseClass}__files__item--full`]: isLast && isOdd,
                            [`${className}__files__item--full`]: isLast && isOdd && className,
                            [`${baseClass}__files__item--last`]: isLast,
                            [`${className}__files__item--last`]: isLast && className,
                            [`${baseClass}__files__item--error`]: error,
                            [`${className}__files__item--error`]: error && className,
                            [`${baseClass}__files__item--show-delete`]: isShowDelete,
                            [`${className}__files__item--show-delete`]: isShowDelete && className,
                        })
                        if (Array.isArray(errorMessage)) {
                            const errorForIndex = errorMessage[index]

                            if (errorForIndex) {
                                if (typeof errorForIndex === 'string') {
                                    error = errorForIndex
                                } else if (typeof errorForIndex === 'object' && errorForIndex !== null) {
                                    error = Object.values(errorForIndex)
                                        .filter((val) => typeof val === 'string' && val.trim())
                                        .join(', ')
                                }
                            }
                        }
                        console.log(error)
                        return (
                            <FileUploaderV2ListItem
                                key={'uid' in file ? `uid-${file.uid}-${error}` : `name-${file.name}-${error}`}
                                file={file}
                                className={classNameItem}
                                fileError={error}
                                // onDelete={onDelete}
                                progress={(progressItems ?? []).find((it) => it.index === index)?.progress}
                                status={(progressItems ?? []).find((it) => it.index === index) && 'uploading'}
                                // status={'error'}
                                isShowDelete={!!onDelete}
                                loading={
                                    'uid' in file
                                        ? loadingIds?.includes(file.uid)
                                        : 'name' in file
                                          ? loadingIds?.includes(file.name)
                                          : false
                                }
                                onDelete={onDeleteMiddleware}
                            />
                        )
                    })}
                    {Array.from({ length: uploadingCount }).map((_, index) => {
                        const isLast = index === files.length + uploadingCount
                        const isOdd = (files.length + uploadingCount) % 2 === 1
                        return (
                            <FileUploaderV2ListItem
                                key={`loading-${index}`}
                                file={{ name: '', size: 0, type: '', uid: `loading-${index}` } as unknown as File}
                                loading={true}
                                onDelete={onDeleteMiddleware}
                                className={clsx(`${baseClass}__files__item ${baseClass}__files__item--loading`, {
                                    [`${baseClass}__files__item--full`]: isLast && isOdd,
                                    [`${baseClass}__files__item--last`]: isLast,
                                    [`${className}__files__item`]: className,
                                    [`${className}__files__item--full`]: isLast && isOdd && className,
                                    [`${className}__files__item--last`]: isLast && className,
                                })}
                            />
                        )
                    })}
                </div>
            )}
            {errorMessageRes && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__errorMessage`, {
                        [`${className}__errorMessage`]: !!className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__errorMessage--icon`, {
                            [`${className}__errorMessage--icon`]: className,
                        })}
                    >
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__errorMessage--text`, {
                            [`${className}__errorMessage--text`]: className,
                        })}
                    >
                        {errorMessageRes}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2AttachmentsUploader2
